/**
 * The API of the backoffice: same origin, typed, no configured address.
 *
 * There is deliberately no `COUNTDOWN_API_BASE_URL` here. The edge routes
 * `/api/*` of the operator domain to the API (ops/compose/dev/nginx.conf and
 * the host vhost do the same), so a relative path is the whole configuration.
 * That also keeps the house rule against hardcoded API URLs without a
 * build-time variable: a Vite app can only bake one in, and the tenant
 * domains of this product are rows in the control plane.
 *
 * The types come from the generated client (`@countdown/api-client`), which is
 * checked in and pinned to its generator (ADR 0001). Nothing here restates a
 * response shape.
 *
 * Cookies travel with every request: the session is a first-party HttpOnly
 * cookie set by the callback, and no token is ever visible to this code. That
 * is the reason the sign-in runs server-side rather than as PKCE in the
 * browser.
 */

import type { components } from "@countdown/api-client";
import {
  createCountdownApiClient,
  type CountdownApiClient,
} from "@countdown/api-client";
import { useCallback, useEffect, useRef, useState } from "react";

import type { DoorOutcome, Frame, UploadRefusal } from "@countdown/door-editor";

import { type DoorDocument, FIELD_NAMES } from "@/lib/door";
import { renewSession } from "@/lib/session-renewal";

export type { DoorOutcome, UploadRefusal };

/**
 * The origin this page was served from, read at runtime, never configured.
 *
 * Not an empty string: `normalizeBaseUrl` refuses one, deliberately, so that a
 * missing deployment variable cannot become a client that quietly talks to
 * nothing. That refusal happens while the module is being evaluated, which is
 * why getting it wrong showed up as an empty `<div id="root">` with an empty
 * console rather than as an error: the module never finished importing, so
 * `createRoot` never ran.
 *
 * `location.origin` is the honest answer for this app: the edge serves the
 * application and routes `/api/*` of the same host to the API, so the address
 * is not a deployment decision here, it is wherever this page came from.
 */
export const client: CountdownApiClient = createCountdownApiClient({
  baseUrl: window.location.origin,
  fetch: renewingFetch,
});

/** Where the server-side sign-in starts. A full page navigation, not a fetch. */
export const SIGN_IN_PATH = "/api/v1/backoffice/login";

const BACKOFFICE_API = "/api/v1/backoffice/";
let signedIn = false;

/**
 * Every request of the backoffice: one that meets an expired session waits for its renewal
 * and is sent once more (`lib/session-renewal.ts`); the page and what was typed stay. Only
 * after a session was seen, so a visitor who never signed in still gets the sign-in screen,
 * and never for "Abmelden", which must not sign anybody in.
 */
export async function renewingFetch(request: Request): Promise<Response> {
  const again = request.clone();
  const response = await fetch(request);
  const path = new URL(request.url).pathname;
  if (!path.startsWith(BACKOFFICE_API)) {
    return response;
  }
  if (response.ok) {
    signedIn = true;
    return response;
  }
  if (response.status !== 401 || !signedIn || path.endsWith("/logout")) {
    return response;
  }
  console.log("[api] session expired, renewing", path);
  await renewSession();
  return fetch(again);
}

/**
 * Query markers that stop the automatic sign-in (D47): `anmeldung` is the way back from
 * one, `abgemeldet` follows "Abmelden", `fehler` a sign-in that failed. Without them an
 * account without a permission would bounce between the identity service and this page.
 */
const SIGN_IN_MARKERS = ["anmeldung", "abgemeldet", "fehler"] as const;

type Here = Pick<Location, "pathname" | "search">;

/**
 * The sign-in that returns to `here`, marked as the way back. The server keeps a return
 * path of at most 300 characters (`safe_return_to`); a longer one would land on "/"
 * without the marker, so it is cut to "/" here, with it.
 */
export function signInUrl(path: string, here: Here): string {
  const query = new URLSearchParams(here.search);
  query.delete("fehler");
  query.delete("abgemeldet");
  query.set("anmeldung", "1");
  const back = `${here.pathname}?${query.toString()}`;
  const target = back.length <= 300 ? back : "/?anmeldung=1";
  return `${path}?return_to=${encodeURIComponent(target)}`;
}

/** Where to sign in without a click, or `null` when this page must not try (see the markers). */
export function autoSignInUrl(path: string, here: Here): string | null {
  const query = new URLSearchParams(here.search);
  return SIGN_IN_MARKERS.some((key) => query.has(key)) ? null : signInUrl(path, here);
}

/**
 * One generic sentence for every failure, like everywhere else in this product.
 * Which of "not signed in", "no permission" and "API unreachable" it was
 * belongs in the log, not on the screen, and the API already answers that way.
 */
export const LOAD_FAILED = "Die Daten konnten nicht geladen werden.";

/** What a live-action screen says on `not_found`: between seasons, not an outage. */
export const NO_LIVE_ACTION = "Keine laufende Aktion.";

/**
 * What a screen is told when its route needs a role this operator does not hold.
 *
 * Its own state, not `error`: the request worked and the answer was "not yours".
 * Rendering that as "the data could not be loaded" would send somebody looking
 * for an outage. And it must not be folded into `unauthenticated` either: that
 * screen offers a sign-in button, which is exactly the wrong advice for a person
 * who is already signed in.
 */
export const ROLE_MISSING =
  "Für diesen Bereich fehlt Ihrer Anmeldung die Berechtigung.";

export type ResourceState<T> =
  | { readonly status: "loading" }
  /** 401: the caller has to sign in, or has no role in this cell. */
  | { readonly status: "unauthenticated" }
  /** 403: signed in, but this route needs a role they do not hold. */
  | { readonly status: "forbidden" }
  /** 404: the named action does not exist, or none is live. Normal between seasons. */
  | { readonly status: "not_found" }
  | { readonly status: "error"; readonly message: string }
  | { readonly status: "ready"; readonly data: T };

/**
 * Fired on every 401 of {@link useResource}. The shell checks its session only once, so a
 * cookie that ran out while a page stood open left that page saying "could not be loaded";
 * the shell listens and asks again, which leads to the sign-in.
 */
export const SESSION_LOST = "countdown:session-lost";

type Loader<T> = (
  api: CountdownApiClient,
) => Promise<{ data?: T; response: Response }>;

/**
 * Load one resource once, and expose the states a screen has to render.
 *
 * "Not signed in" and "no such action" are not errors, they are normal states
 * with their own answer (a sign-in button, "Keine laufende Aktion."). Collapsing
 * them into `error` is how an application ends up telling somebody that
 * something broke when it did not.
 *
 * A reload keeps the last `ready` or `not_found` answer until the next one
 * arrives. Dropping back to `loading` would unmount the screen below it, and
 * with it whatever the operator had open. `pending` is true while a request
 * runs, so a screen can hold back buttons that act on rows about to change.
 */
export function useResource<T>(
  load: Loader<T>,
): ResourceState<T> & {
  readonly reload: () => void;
  readonly pending: boolean;
} {
  const [state, setState] = useState<ResourceState<T>>({ status: "loading" });
  const [pending, setPending] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const loaded = useRef(load);

  const reload = useCallback(() => {
    setAttempt((value) => value + 1);
  }, []);

  useEffect(() => {
    let active = true;
    // A new loader is another resource (another action): its old data must not stand in.
    const sameResource = loaded.current === load;
    loaded.current = load;
    setPending(true);
    setState((current) =>
      sameResource &&
      (current.status === "ready" || current.status === "not_found")
        ? current
        : { status: "loading" },
    );

    load(client)
      .then(({ data, response }) => {
        if (!active) {
          return;
        }
        if (response.status === 401) {
          setState({ status: "unauthenticated" });
          window.dispatchEvent(new Event(SESSION_LOST));
          return;
        }
        if (response.status === 403) {
          setState({ status: "forbidden" });
          return;
        }
        if (response.status === 404) {
          setState({ status: "not_found" });
          return;
        }
        if (data === undefined) {
          console.error("[api] request failed", response.status);
          setState({ status: "error", message: LOAD_FAILED });
          return;
        }
        setState({ status: "ready", data });
      })
      .catch((error: unknown) => {
        if (!active) {
          return;
        }
        console.error("[api] request threw", error);
        setState({ status: "error", message: LOAD_FAILED });
      })
      .finally(() => {
        if (active) {
          setPending(false);
        }
      });

    return () => {
      active = false;
    };
    // `load` belongs in here: callers pass a module-level function or one held
    // in `useCallback`, so the reference changes only when its action does.
    // `attempt` is the explicit reload trigger.
  }, [load, attempt]);

  return { ...state, reload, pending };
}

export function loadSession(api: CountdownApiClient) {
  return api.GET("/api/v1/backoffice/session");
}

export function loadOverview(api: CountdownApiClient) {
  return api.GET("/api/v1/backoffice/overview");
}

export type NotificationList = components["schemas"]["NotificationListView"];

/** The bell (D56): the newest entries this operator's permissions see, and the unread ones. */
export function loadNotifications(api: CountdownApiClient) {
  return api.GET("/api/v1/backoffice/notifications");
}

/** Mark `ids`, or every entry of the bell, read. The new list, or `null` when it failed. */
export async function readNotifications(
  ids?: readonly string[],
): Promise<NotificationList | null> {
  try {
    const { data, response } = await client.POST("/api/v1/backoffice/notifications/read", {
      body: ids === undefined ? {} : { ids: [...ids] },
    });
    if (data === undefined) {
      console.error("[api] notifications read failed", response.status);
      return null;
    }
    return data;
  } catch (caught: unknown) {
    console.error("[api] notifications read threw", caught);
    return null;
  }
}

/**
 * Every per-action call names its action as `?action_id=`. The field list must;
 * the others fall back to the live action without one, which is what the
 * rehearsal and the screens that predate the action pages rely on.
 */
export function loadFields(api: CountdownApiClient, actionId: string) {
  return api.GET("/api/v1/backoffice/fields", {
    params: { query: { action_id: actionId } },
  });
}

export function loadEditorialBookings(
  api: CountdownApiClient,
  actionId?: string,
) {
  return api.GET("/api/v1/backoffice/bookings", {
    params: { query: { action_id: actionId } },
  });
}

/**
 * End the operator session and reload into the signed-out state.
 *
 * A reload rather than clearing React state: the session is an HttpOnly cookie
 * this code cannot see, so "am I signed in" is only ever answered by asking the
 * API. Re-mounting from scratch is that question, and it cannot disagree with
 * the server the way a local flag would.
 *
 * The reload happens even when the request failed. A sign-out that leaves the
 * screen exactly as it was is the one outcome somebody walking away from a
 * shared machine must not get; if the cookie survived, the reload shows the
 * surface again, which is at least honest about it.
 */
export async function signOut(): Promise<void> {
  try {
    await client.POST("/api/v1/backoffice/logout", {});
  } catch (error: unknown) {
    console.error("[api] sign-out failed", error);
  }
  // The identity service still knows the person; the marker keeps the start page from
  // signing them straight back in.
  window.location.assign("/?abgemeldet=1");
}

/**
 * The editorial writes on one booking (concept 9.7.2, H2, E4). Paths, so one caller can drive
 * all of them. The booking's own preview link stays with the sponsor flow; the backoffice
 * frames the participant page through {@link createActionPreview} instead.
 */
export type EditorialAction =
  | "/api/v1/backoffice/bookings/review"
  | "/api/v1/backoffice/bookings/approve"
  | "/api/v1/backoffice/bookings/withdraw-approval"
  | "/api/v1/backoffice/bookings/request-changes"
  | "/api/v1/backoffice/bookings/send-to-sponsor"
  | "/api/v1/backoffice/bookings/remind-sponsor"
  | "/api/v1/backoffice/bookings/publish-without-sponsor"
  | "/api/v1/backoffice/bookings/discard-correction";

export type ActionOutcome =
  | {
      readonly ok: true;
      /** The sponsor's invitation link, only from "Einladungslink anzeigen". */
      readonly inviteUrl?: string;
      readonly message?: string;
    }
  | { readonly ok: false; readonly message: string };

export type SponsorDetail = components["schemas"]["SponsorDetailView"];
export type SponsorStatus = components["schemas"]["SponsorStatus"];

/**
 * Perform one editorial action and report what happened, in one sentence.
 *
 * The message comes from the API and is shown as-is, which is the opposite of the
 * rule for participant-facing text. It is deliberate: the reason a transition was
 * refused ("diese Belegung ist im Zustand …") is what a colleague needs, and the
 * endpoint is behind a role, so there is nobody here to learn the state machine
 * from it who could not read it anyway.
 *
 * No optimistic update. The caller reloads the list, because a release changes
 * more than the row it was clicked on (the fulfillment status, the image, the
 * deadlines) and a hand-patched row would drift from the server's answer.
 */
export async function runEditorialAction(
  path: EditorialAction,
  body: {
    readonly field_position: number;
    readonly comment?: string;
    /** Request-changes and send-to-sponsor: `false` sends no mail to the sponsor. */
    readonly notify?: boolean;
    /** Publish-without-sponsor: why, 3 to 500 characters, for the audit log (E3). */
    readonly reason?: string;
  },
  actionId?: string,
): Promise<ActionOutcome> {
  try {
    // The generated client types each path separately; this helper takes them as one
    // union, so the body is cast once here rather than at every call site.
    const { data, response, error } = await client.POST(
      path as never,
      { body, params: { query: { action_id: actionId } } } as never,
    );

    if (response.status === 401) {
      return {
        ok: false,
        message: "Die Anmeldung ist abgelaufen. Bitte neu anmelden.",
      };
    }
    if (response.status === 403) {
      return { ok: false, message: ROLE_MISSING };
    }
    if (data === undefined) {
      const detail = (error as { detail?: unknown } | undefined)?.detail;
      console.error(
        "[api] editorial action failed",
        path,
        response.status,
        detail,
      );
      return {
        ok: false,
        message: typeof detail === "string" && detail ? detail : LOAD_FAILED,
      };
    }
    return { ok: true };
  } catch (error: unknown) {
    console.error("[api] editorial action threw", path, error);
    return { ok: false, message: LOAD_FAILED };
  }
}

/**
 * Release a contribution whose image or logo the scan has not cleared (tenant admin only).
 *
 * Its own function: the body carries a reason instead of a comment, and the reason ends up in
 * the audit row. The server refuses a rejected image whatever the reason says.
 */
export async function approveUnscanned(
  position: number,
  reason: string,
  actionId?: string,
): Promise<ActionOutcome> {
  return post(
    "/api/v1/backoffice/bookings/approve-unscanned",
    { field_position: position, reason },
    actionId,
  );
}

export type EditorialBooking = components["schemas"]["EditorialBookingView"];

/**
 * Save the whole door (concept 9.3, 9.7.4, D23): every slide and question in order, the quiz
 * texts and the prize.
 *
 * Its own function rather than {@link post}: the editor needs the answer. A new question
 * exists only there with its id, and the server drops empty rows and a correct key that no
 * longer names an answer. A form that kept what it sent would write each new question again
 * on the next save.
 */
export async function saveBookingDocument(
  body: components["schemas"]["BookingContentRequest"],
  actionId?: string,
): Promise<DoorOutcome> {
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/bookings/content",
      { params: { query: { action_id: actionId } }, body },
    );
    if (data === undefined) {
      console.error("[api] door save failed", response.status);
      return { ok: false, message: contentRefusal(response.status, error) };
    }
    return { ok: true, booking: data };
  } catch (caught: unknown) {
    console.error("[api] door save threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/** The places of a door's request, as the editor names them. */
const CONTENT_FIELDS: Readonly<Record<string, string>> = FIELD_NAMES;

/** `["body", "slides", 1, "button", "url"]` as "Folie 2, Adresse des Knopfs". */
function contentPlace(loc: readonly unknown[]): string {
  const parts: string[] = [];
  // The first entry is the request part ("body"), not the slide's text of the same name.
  loc.slice(1).forEach((part, index, rest) => {
    const before = rest[index - 1];
    if (typeof part === "number") {
      if (before === "slides") {
        parts.push(`Folie ${String(part + 1)}`);
      } else if (before === "questions") {
        parts.push(`Frage ${String(part + 1)}`);
      }
      return;
    }
    const label = typeof part === "string" ? CONTENT_FIELDS[part] : undefined;
    if (label !== undefined) {
      parts.push(label);
    }
  });
  return parts.join(", ");
}

/**
 * A refused door save, each problem with its place: "Folie 2, Text: Überschriften sind nicht
 * erlaubt."
 *
 * A door has up to five slides and three questions, and the one parser of their texts is on
 * the server; a sentence without its place would leave the editor searching. The API's own
 * validators write German and pass through; the built-in ones write English and are named
 * by their kind.
 */
export function contentRefusal(status: number, error: unknown): string {
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  if (status !== 422 || !Array.isArray(detail) || detail.length === 0) {
    return outcomeMessage(status, error);
  }
  const own = "Value error, ";
  return detail
    .map((entry: unknown) => {
      const { loc, msg, type } = (entry ?? {}) as {
        loc?: unknown;
        msg?: unknown;
        type?: unknown;
      };
      const place = Array.isArray(loc) ? contentPlace(loc) : "";
      let text = "Die Eingabe ist ungültig.";
      if (typeof msg === "string" && msg.startsWith(own)) {
        text = msg.slice(own.length);
      } else if (type === "string_too_long") {
        text = "Der Text ist zu lang.";
      }
      return place === "" ? text : `${place}: ${text}`;
    })
    .join(" ");
}

/**
 * Upload or replace the picture in one slot of a door: `slide:N`, `question:N` or `prize`.
 *
 * Built the same way the sponsor portal builds its own multipart post: `bodySerializer`
 * assembles the form data so the request still travels through the generated client
 * (conventions: no raw fetch). Without it openapi-fetch JSON-encodes the body and the file
 * arrives as the string "[object File]".
 */
export async function uploadBookingMedia(
  upload: { readonly position: number; readonly slot: string } & PictureSource,
  actionId?: string,
): Promise<DoorOutcome> {
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/bookings/media",
      {
        params: { query: { action_id: actionId } },
        body: { field_position: upload.position, slot: upload.slot, ...pictureFields(upload) },
        bodySerializer(body: Record<string, unknown> | undefined) {
          const payload = logoForm(body);
          payload.append("field_position", String(body?.field_position));
          payload.append("slot", String(body?.slot));
          return payload;
        },
      },
    );
    if (data === undefined) {
      console.error("[api] door media upload failed", upload.slot, response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, booking: data };
  } catch (caught: unknown) {
    console.error("[api] door media upload threw", upload.slot, caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/**
 * What a picture upload sends: a new file with its text and, cut before the save, its frame
 * (D88), or the file a place shows and the frame to cut its original again (D44).
 */
export type PictureSource =
  | {
      readonly file: File;
      readonly altText: string;
      readonly unscannedReason?: string;
      readonly crop?: Frame;
    }
  | { readonly assetId: string; readonly crop: Frame; readonly unscannedReason?: string };

/** The fields of a picture source as the API names them. */
function pictureFields(source: PictureSource): {
  file?: string;
  alt_text: string;
  asset_id?: string;
  crop?: string;
  accept_unscanned_reason?: string;
} {
  const reason = { accept_unscanned_reason: source.unscannedReason };
  const crop = source.crop === undefined ? {} : { crop: JSON.stringify(source.crop) };
  return "file" in source
    ? // openapi-typescript maps `format: binary` to `string`; the serializer puts the File.
      { file: source.file as unknown as string, alt_text: source.altText, ...crop, ...reason }
    : { asset_id: source.assetId, ...crop, alt_text: "", ...reason };
}

/** Where the cut dialog loads the original behind a picture (D44). */
export function originalAddress(assetId: string): string {
  return `/api/v1/backoffice/assets/${encodeURIComponent(assetId)}/original`;
}

/** Where the configurator's preview loads a stored file itself, released or not (D88). */
export function assetAddress(assetId: string): string {
  return `/api/v1/backoffice/assets/${encodeURIComponent(assetId)}`;
}

/**
 * Form data for a logo: the file, and the description and the reason for taking it unscanned
 * only when one was typed. A new cut (D44) sends no file but the shown file and the frame.
 */
function logoForm(body: Record<string, unknown> | undefined): FormData {
  const payload = new FormData();
  // Not `instanceof File`: a file chosen in the preview's frame belongs to the frame's window.
  if (Object.prototype.toString.call(body?.file) === "[object File]") {
    payload.append("file", body?.file as File);
  }
  for (const key of ["alt_text", "accept_unscanned_reason", "asset_id", "crop"]) {
    const value = body?.[key];
    if (typeof value === "string" && value.trim() !== "") {
      payload.append(key, value.trim());
    }
  }
  return payload;
}

function uploadRefusal(status: number, error: unknown): UploadRefusal {
  return { ok: false, message: outcomeMessage(status, error), unscanned: status === 409 };
}

export type LogoOutcome = { readonly ok: true; readonly detail: SponsorDetail } | UploadRefusal;

/**
 * The logo on a sponsor's calendar doors. Public at once, since the editors are the ones who
 * release, provided the security check gave a clean verdict; otherwise the API refuses and
 * says so, and nothing is stored.
 */
export async function uploadSponsorLogo(
  sponsorId: string,
  source: PictureSource,
): Promise<LogoOutcome> {
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/sponsors/{sponsor_id}/logo",
      {
        params: { path: { sponsor_id: sponsorId } },
        body: pictureFields(source),
        bodySerializer: logoForm,
      },
    );
    if (data === undefined) {
      console.error("[api] sponsor logo upload failed", response.status);
      return uploadRefusal(response.status, error);
    }
    return { ok: true, detail: data };
  } catch (caught: unknown) {
    console.error("[api] sponsor logo upload threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

export type ImageOutcome = { readonly ok: true } | UploadRefusal;

/**
 * The picture behind the action's unbooked doors, also its browser icon (D29). Public at once
 * with a clean security check, like the brand logo; otherwise the API refuses and says so.
 */
export async function uploadPlaceholderImage(
  actionId: string,
  source: PictureSource,
): Promise<ImageOutcome> {
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/actions/placeholder-image",
      {
        params: { query: { action_id: actionId } },
        body: pictureFields(source),
        bodySerializer: logoForm,
      },
    );
    if (data === undefined && !response.ok) {
      console.error("[api] placeholder image upload failed", response.status);
      return uploadRefusal(response.status, error);
    }
    return { ok: true };
  } catch (caught: unknown) {
    console.error("[api] placeholder image upload threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/**
 * The fallback content of an action (9.3, D88) as the door editor reads it: one slide of its
 * text and picture, the shape the participant page shows. Position 0, so the ids of its inputs
 * differ from those of the door configured beside it.
 */
export function fallbackDoor(detail: ActionDetail): DoorDocument {
  const body = detail.fallback_content_text ?? null;
  const picture = detail.fallback_image ?? null;
  // A picture, never a video: nothing a video needs is asked for.
  const media = picture === null ? null : { ...picture, needs_no_captions: false };
  return {
    field_position: 0,
    slides: body === null && media === null ? [] : [{ title: null, body, media, button: null }],
    quiz: { questions: [] },
    prize_image: null,
    prize_description: null,
    prize_quantity: 1,
    prize_digital: false,
  };
}

export type FallbackOutcome = { readonly ok: true; readonly detail: ActionDetail } | UploadRefusal;

/** One write of the fallback content; each answers with the action as stored. */
async function fallbackWrite(
  what: string,
  write: () => Promise<{ data?: ActionDetail; response: Response; error?: unknown }>,
): Promise<FallbackOutcome> {
  try {
    const { data, response, error } = await write();
    if (data === undefined) {
      console.error("[api] fallback write failed", what, response.status);
      return uploadRefusal(response.status, error);
    }
    return { ok: true, detail: data };
  } catch (caught: unknown) {
    console.error("[api] fallback write threw", what, caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/** The text of the fallback content; empty removes it. */
export function saveFallbackText(actionId: string, text: string): Promise<FallbackOutcome> {
  return fallbackWrite("text", () =>
    client.POST("/api/v1/backoffice/actions/fallback", {
      params: { query: { action_id: actionId } },
      body: { text },
    }),
  ).then((outcome) => (outcome.ok ? outcome : { ok: false, message: outcome.message }));
}

/**
 * The picture of the fallback content: a new file, or a new cut of the one shown. Public at
 * once, so a file the security check could not clear is refused unless a reason is given.
 */
export function uploadFallbackImage(actionId: string, source: PictureSource): Promise<FallbackOutcome> {
  return fallbackWrite("image", () =>
    client.POST("/api/v1/backoffice/actions/fallback-image", {
      params: { query: { action_id: actionId } },
      body: pictureFields(source),
      bodySerializer: logoForm,
    }),
  );
}

/** The fallback content without its picture. */
export function removeFallbackImage(actionId: string): Promise<FallbackOutcome> {
  return fallbackWrite("image removal", () =>
    client.POST("/api/v1/backoffice/actions/fallback-image/remove", {
      params: { query: { action_id: actionId } },
    }),
  ).then((outcome) => (outcome.ok ? outcome : { ok: false, message: outcome.message }));
}

/**
 * The example view of an action (D32): the tab "Beispielansicht". Every write answers with the
 * whole tab as stored, so the page takes it as its new state rather than reloading.
 */
export type ExampleEdit = components["schemas"]["ExampleEditView"];
export type ExampleSettingsRequest = components["schemas"]["ExampleSettingsRequest"];

export type ExampleOutcome =
  | { readonly ok: true; readonly view: ExampleEdit }
  | UploadRefusal;

export function loadExample(api: CountdownApiClient, actionId: string) {
  return api.GET("/api/v1/backoffice/actions/example-view", {
    params: { query: { action_id: actionId } },
  });
}

/**
 * A door of the example as the door editor reads it, every part present: the one at
 * `position` (1 to the example's day, D37), else the open one.
 */
export function exampleDoor(view: ExampleEdit, position?: number): DoorDocument {
  const door =
    (position === undefined ? undefined : view.doors?.find((entry) => entry.field_position === position)) ??
    view.door;
  return {
    field_position: door.field_position,
    slides: door.slides ?? [],
    quiz: door.quiz ?? { questions: [] },
    prize_image: door.prize_image ?? null,
    prize_description: door.prize_description ?? null,
    prize_quantity: door.prize_quantity ?? 1,
    prize_digital: false,
  };
}

/** One write of the tab, with the shared status handling. */
async function exampleWrite(
  what: string,
  write: () => Promise<{ data?: ExampleEdit; response: Response; error?: unknown }>,
  refusal: (status: number, error: unknown) => string = outcomeMessage,
): Promise<ExampleOutcome> {
  try {
    const { data, response, error } = await write();
    if (data === undefined) {
      console.error("[api] example write failed", what, response.status);
      return {
        ok: false,
        message: refusal(response.status, error),
        unscanned: response.status === 409,
      };
    }
    return { ok: true, view: data };
  } catch (caught: unknown) {
    console.error("[api] example write threw", what, caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/** Which door stands open and what the other doors show. */
export function saveExampleSettings(
  actionId: string,
  body: ExampleSettingsRequest,
): Promise<ExampleOutcome> {
  return exampleWrite("settings", () =>
    client.POST("/api/v1/backoffice/actions/example-view", {
      params: { query: { action_id: actionId } },
      body,
    }),
  ).then(withoutUnscanned);
}

/**
 * The link with the key typed in, a random one for an empty key, or none. The old link stops
 * working either way; a key another action holds is refused with a sentence.
 */
export function switchExampleLink(
  actionId: string,
  enabled: boolean,
  key = "",
): Promise<ExampleOutcome> {
  return exampleWrite("link", () =>
    client.POST("/api/v1/backoffice/actions/example-view/link", {
      params: { query: { action_id: actionId } },
      body: { enabled, key: key.trim() || null },
    }),
  ).then(withoutUnscanned);
}

/**
 * The answer of a write on a door of the example as the door editor takes it: that door as
 * stored. The page keeps the whole tab from the same answer ({@link ExampleOutcome}).
 */
export function doorOutcome(outcome: ExampleOutcome, position?: number): DoorOutcome {
  return outcome.ok ? { ok: true, booking: exampleDoor(outcome.view, position) } : outcome;
}

/** A door of the example as the door editor sends it, with each refusal at its place. */
export async function saveExampleDoor(
  actionId: string,
  body: components["schemas"]["BookingContentRequest"],
): Promise<ExampleOutcome> {
  const outcome = await exampleWrite(
    "door",
    () =>
      client.POST("/api/v1/backoffice/actions/example-view/door", {
        params: { query: { action_id: actionId } },
        body,
      }),
    contentRefusal,
  );
  return withoutUnscanned(outcome);
}

/**
 * One picture for one place of a door of the example: `slide:N`, `question:N` or `prize`, on
 * the door `position` names (the open one without it, D37). Public at once, so a file the
 * security check could not clear is refused (`unscanned`) unless a tenant admin names a reason.
 */
export function uploadExampleMedia(
  actionId: string,
  upload: { readonly position?: number; readonly slot: string } & PictureSource,
): Promise<ExampleOutcome> {
  return exampleWrite("media", () =>
    client.POST("/api/v1/backoffice/actions/example-view/media", {
      params: { query: { action_id: actionId } },
      body: { position: upload.position ?? null, slot: upload.slot, ...pictureFields(upload) },
      bodySerializer(body: Record<string, unknown> | undefined) {
        const payload = logoForm(body);
        payload.append("slot", String(body?.slot));
        if (typeof body?.position === "number") {
          payload.append("position", String(body.position));
        }
        return payload;
      },
    }),
  );
}

/** The logo of the partner on one door; the name is its description for screen readers. */
export function uploadExamplePartnerLogo(
  actionId: string,
  upload: { readonly position: number; readonly name: string } & PictureSource,
): Promise<ExampleOutcome> {
  return exampleWrite("partner logo", () =>
    client.POST("/api/v1/backoffice/actions/example-view/partner-logo", {
      params: { query: { action_id: actionId } },
      // The name is the logo's text; the route takes no other.
      body: { position: upload.position, name: upload.name, ...pictureFields(upload), alt_text: "" },
      bodySerializer(body: Record<string, unknown> | undefined) {
        const payload = logoForm(body);
        payload.append("position", String(body?.position));
        payload.append("name", String(body?.name));
        return payload;
      },
    }),
  );
}

/** A 409 of a save is a finished action or a foreign picture, never an unscanned file. */
function withoutUnscanned(outcome: ExampleOutcome): ExampleOutcome {
  return outcome.ok ? outcome : { ok: false, message: outcome.message };
}

/**
 * One door's own number (D29) and the place of its logo (D31), saved together; `null`
 * returns each to its default: the action's number, the built-in logo place.
 */
export async function saveFieldDoorDesign(
  actionId: string,
  position: number,
  design: components["schemas"]["FieldDoorDesignRequest"],
): Promise<ActionOutcome> {
  try {
    const { data, response, error } = await client.POST("/api/v1/backoffice/fields/door-design", {
      params: { query: { action_id: actionId, position } },
      body: design,
    });
    if (data === undefined) {
      console.error("[api] door design save failed", position, response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true };
  } catch (caught: unknown) {
    console.error("[api] door design save threw", position, caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/**
 * How the doors of an action open their window (D37): page or dialog, the standard layout and
 * whether sponsors may change it. The publisher's alone; answers with the action as stored.
 */
export async function saveDoorWindow(
  actionId: string,
  body: components["schemas"]["DoorWindowRequest"],
): Promise<
  | { readonly ok: true; readonly detail: ActionDetail }
  | { readonly ok: false; readonly message: string }
> {
  try {
    const { data, response, error } = await client.POST("/api/v1/backoffice/actions/door-window", {
      params: { query: { action_id: actionId } },
      body,
    });
    if (data === undefined) {
      console.error("[api] door window save failed", response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, detail: data };
  } catch (caught: unknown) {
    console.error("[api] door window save threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/**
 * One door's own layout and its own sponsor switch (D37); `null` follows the action. In
 * every state of the booking; answers with the booking as stored.
 */
export async function saveFieldDoorLayout(
  actionId: string,
  body: components["schemas"]["FieldDoorLayoutRequest"],
): Promise<
  | { readonly ok: true; readonly booking: EditorialBooking }
  | { readonly ok: false; readonly message: string }
> {
  try {
    const { data, response, error } = await client.POST("/api/v1/backoffice/fields/door-layout", {
      params: { query: { action_id: actionId } },
      body,
    });
    if (data === undefined) {
      console.error("[api] door layout save failed", body.field_position, response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, booking: data };
  } catch (caught: unknown) {
    console.error("[api] door layout save threw", body.field_position, caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/**
 * The address of a released image on this surface. The backoffice domain forwards `/api/`
 * to the API, so the media route answers here as on the calendar; only released files are
 * served, which are the only ones these views name.
 */
export function mediaSrc(path: string): string {
  return `/api/v1/media/${encodeURIComponent(path)}`;
}

export type PreviewOutcome =
  | {
      readonly ok: true;
      readonly previewUrl: string;
      readonly expiresAt: string;
    }
  | { readonly ok: false };

/**
 * Mint a link that opens every field of one action, each on its own day, from the draft on.
 * With `fieldPosition` the link opens that door's page instead of the calendar.
 *
 * Its own function rather than another {@link EditorialAction}: that union is calls that
 * name a booking by position, and this one needs no booking: the point of it is also the
 * fields that have none. The link is never logged: it opens unreleased contributions.
 */
export async function createActionPreview(
  actionId: string,
  fieldPosition?: number,
): Promise<PreviewOutcome> {
  try {
    const { data, response } = await client.POST(
      "/api/v1/backoffice/preview",
      {
        params: { query: { action_id: actionId } },
        body: { field_position: fieldPosition },
      },
    );
    if (data === undefined) {
      console.error("[api] action preview failed", response.status);
      return { ok: false };
    }
    return {
      ok: true,
      previewUrl: data.preview_url,
      expiresAt: data.expires_at,
    };
  } catch (caught: unknown) {
    console.error("[api] action preview threw", caught);
    return { ok: false };
  }
}

/**
 * Sponsor administration (concept 9.7.1).
 *
 * Five calls rather than one generic writer, because the four writes have four different
 * bodies and one of them answers with a bearer token. A union wide enough to describe all of
 * them would describe none.
 *
 * `loadSponsorDetail` is the only place in this application that reads a contact address, and
 * it is a request for ONE company. The list keeps reporting "hinterlegt / fehlt": a screen
 * that sits open all day has no business holding addresses.
 */
export async function loadSponsorDetail(
  sponsorId: string,
): Promise<SponsorDetail | null> {
  try {
    const { data, response } = await client.GET(
      "/api/v1/backoffice/sponsors/{sponsor_id}",
      {
        params: { path: { sponsor_id: sponsorId } },
      },
    );
    if (data === undefined) {
      console.error("[api] sponsor detail failed", response.status);
      return null;
    }
    return data;
  } catch (caught: unknown) {
    console.error("[api] sponsor detail threw", caught);
    return null;
  }
}

export type SaveSponsorOutcome =
  | { readonly ok: true; readonly detail: SponsorDetail }
  | { readonly ok: false; readonly message: string };

/**
 * Save a sponsor and hand back what was stored.
 *
 * Its own function rather than {@link post}: the screen needs the answer. For a new
 * company the id exists only in it, and without the id the screen cannot open the
 * company it just created, which is where the next step lives. Saving sends no
 * mail; the invitation belongs to a placement, and until 23.09.2026 nothing on
 * the screen said so or led there.
 */
export async function saveSponsor(body: {
  /** The sponsor being edited; with it a changed company name renames that sponsor. */
  readonly sponsor_id?: string | null;
  readonly company_name: string;
  readonly contact_name?: string | null;
  readonly contact_email?: string | null;
  readonly industry_category?: string | null;
  /** Leads, their list and file in the portal, and the figures (D90); stored whole. */
  readonly services?: components["schemas"]["SponsorServices"] | null;
  /** Where the sponsor deviates from the action (E2, E6); the keys left out follow it. */
  readonly sponsor_rights?: components["schemas"]["SponsorRightsOverride"] | null;
}): Promise<SaveSponsorOutcome> {
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/sponsors",
      { body },
    );
    if (data === undefined) {
      console.error("[api] sponsor save failed", response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, detail: data };
  } catch (caught: unknown) {
    console.error("[api] sponsor save threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

export async function setSponsorStatus(
  sponsorId: string,
  status: SponsorStatus,
): Promise<ActionOutcome> {
  return post("/api/v1/backoffice/sponsors/status", {
    sponsor_id: sponsorId,
    status,
  });
}

/**
 * Release the logo the sponsor uploaded, without releasing a contribution (D40). The server
 * refuses a logo without a clean scan and says so.
 */
export async function publishSponsorLogo(sponsorId: string): Promise<ActionOutcome> {
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/sponsors/{sponsor_id}/logo/publish",
      { params: { path: { sponsor_id: sponsorId } } },
    );
    if (data === undefined) {
      console.error("[api] sponsor logo release failed", response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, message: "Logo freigegeben." };
  } catch (caught: unknown) {
    console.error("[api] sponsor logo release threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/**
 * End one person's access to the sponsor portal (D34) and hand back the sponsor as stored now.
 * The other accounts of the company stay; for a new access the person needs a new invitation.
 */
export async function revokeSponsorAccount(
  sponsorId: string,
  accountId: string,
): Promise<SaveSponsorOutcome> {
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/sponsors/accounts/revoke",
      { body: { sponsor_id: sponsorId, sponsor_account_id: accountId } },
    );
    if (data === undefined) {
      console.error("[api] account revoke failed", response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, detail: data };
  } catch (caught: unknown) {
    console.error("[api] account revoke threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/**
 * "Weitere Person einladen": an invitation that sets up one person's own access (D34).
 *
 * Without an address (empty or blank) the contact address is invited. The server's sentence
 * is passed through; when nothing was queued (no contact address, or no mail may go to the
 * address) it is a refusal, because the person at the screen has to give another address.
 */
export async function sendAccessInvitation(
  sponsorId: string,
  email?: string,
  actionId?: string,
): Promise<ActionOutcome> {
  const address = email?.trim() ?? "";
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/sponsors/access-invitation",
      {
        params: { query: { action_id: actionId } },
        body: { sponsor_id: sponsorId, email: address === "" ? null : address },
      },
    );
    if (data === undefined) {
      console.error("[api] access invitation failed", response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return data.queued
      ? { ok: true, message: data.message }
      : { ok: false, message: data.message };
  } catch (caught: unknown) {
    console.error("[api] access invitation threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/** Keep a note about a sponsor for the colleagues (D93); the sponsor never sees it. */
export async function addSponsorNote(sponsorId: string, body: string): Promise<ActionOutcome> {
  const result = await post(`/api/v1/backoffice/sponsors/${sponsorId}/notes`, { body: body.trim() });
  return result.ok ? { ok: true, message: "Notiz gespeichert." } : result;
}

/**
 * Free a booked field again. The reason is required and lands in the audit log.
 *
 * The counterpart of `placeBooking`, whose 409 has always said the booking must be released
 * deliberately; until 18.09.2026 there was no way to do that outside SQL.
 */
export async function releaseBooking(
  body: {
    readonly field_position: number;
    readonly reason: string;
  },
  actionId?: string,
): Promise<ActionOutcome> {
  return post("/api/v1/backoffice/bookings/release", body, actionId);
}

/** Place a sponsor on a field; with `actionId` also on a draft, before the go-live. */
export async function placeBooking(
  body: {
    readonly field_position: number;
    readonly sponsor_id: string;
  },
  actionId?: string,
): Promise<ActionOutcome> {
  return post("/api/v1/backoffice/bookings/placement", body, actionId);
}

/**
 * Send the invitation, or say why nothing went out.
 *
 * Its own function rather than {@link post}: the endpoint answers 200 for two non-events
 * ("already queued", "no address"), and the message is the whole content of the answer.
 */
export async function sendInvitation(
  fieldPosition: number,
  actionId?: string,
): Promise<ActionOutcome> {
  return invitationCall(
    "/api/v1/backoffice/bookings/invitation",
    fieldPosition,
    actionId,
  );
}

/** The link itself, for the support call that starts with "no mail arrived". */
export async function showInvitationLink(
  fieldPosition: number,
  actionId?: string,
): Promise<ActionOutcome> {
  return invitationCall(
    "/api/v1/backoffice/bookings/invitation-link",
    fieldPosition,
    actionId,
  );
}

async function invitationCall(
  path:
    | "/api/v1/backoffice/bookings/invitation"
    | "/api/v1/backoffice/bookings/invitation-link",
  fieldPosition: number,
  actionId: string | undefined,
): Promise<ActionOutcome> {
  try {
    const { data, response, error } = await client.POST(path, {
      params: { query: { action_id: actionId } },
      body: { field_position: fieldPosition },
    });
    if (data === undefined) {
      console.error("[api] invitation failed", path, response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    // The server's sentence, passed through: "already queued" and "no address" are different
    // facts and only the second one is something to act on.
    return {
      ok: true,
      message: data.message,
      inviteUrl: data.invite_url ?? undefined,
    };
  } catch (caught: unknown) {
    console.error("[api] invitation threw", path, caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/**
 * The emergency stop (concept 9.6.4, Bereitschaftsplan §2.3).
 *
 * One function for all eight switches, because they genuinely share a body: a reason, plus
 * the thing being switched. That is the opposite of the sponsor calls above, and the reason
 * is the same in both cases: the shape of the request, not a preference for fewer functions.
 *
 * The answer is always 200 with a sentence, including "was already stopped". During an
 * incident two people throw the same switch within a minute, and an error status would read
 * as "it did not work".
 *
 * `actionId` names the action of the field and action switches (D36: several run side by side);
 * the participant switches act on the whole cell and ignore it.
 */
export async function pullEmergencySwitch(
  switchName:
    | "pause-action"
    | "resume-action"
    | "stop-dispatch"
    | "resume-dispatch"
    | "depublish-field"
    | "republish-field"
    | "block-participant"
    | "unblock-participant",
  body: {
    readonly reason: string;
    readonly field_position?: number;
    readonly participant_id?: string;
    readonly email?: string;
  },
  actionId?: string,
): Promise<ActionOutcome> {
  try {
    const { data, response, error } = await client.POST(
      `/api/v1/backoffice/emergency/${switchName}` as never,
      { body, params: { query: { action_id: actionId } } } as never,
    );
    if (data === undefined) {
      console.error(
        "[api] emergency switch failed",
        switchName,
        response.status,
      );
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, message: (data as { message: string }).message };
  } catch (caught: unknown) {
    console.error("[api] emergency switch threw", switchName, caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

export function loadEmergencyStatus(api: CountdownApiClient, actionId: string) {
  return api.GET("/api/v1/backoffice/emergency/status", {
    params: { query: { action_id: actionId } },
  });
}

export type AuditEntry = components["schemas"]["AuditEntryView"];

export interface AuditFilter {
  readonly days: number;
  readonly limit: number;
  readonly event?: string;
  readonly object_type?: string;
  readonly object_id?: string;
}

/** The cell's audit log, newest first. Filters follow `countdown-admin audit list`. */
export function loadAuditLog(api: CountdownApiClient, filter: AuditFilter) {
  return api.GET("/api/v1/backoffice/audit", { params: { query: filter } });
}

export type PlatformAccess = components["schemas"]["PlatformAccessView"];

/** What the platform operation did in this cell (support sign-ins, platform writes). */
export function loadPlatformAccess(
  api: CountdownApiClient,
  filter: Pick<AuditFilter, "days" | "limit">,
) {
  return api.GET("/api/v1/backoffice/platform-access", {
    params: { query: { days: filter.days, limit: filter.limit } },
  });
}

export type ParticipantRecord = components["schemas"]["ParticipantRecordView"];

/**
 * The records behind an id or an address: counts, block and erasure obstacles. An address can
 * stand behind several participants (an account and the route without one), so it is a list.
 * The server writes an audit row for every record. The address goes in the body only and is
 * never logged here.
 */
export async function lookupParticipant(
  body: { readonly participant_id: string } | { readonly email: string },
): Promise<
  | { readonly ok: true; readonly records: readonly ParticipantRecord[] }
  | { readonly ok: false; readonly message: string }
> {
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/participants/lookup",
      { body },
    );
    if (data === undefined) {
      console.error("[api] participant lookup failed", response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, records: data };
  } catch (caught: unknown) {
    console.error("[api] participant lookup threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/** Erase one participant by id (Art. 17). Not reversible; a 409 names what stands against it. */
export async function eraseParticipant(
  participantId: string,
  reason: string,
): Promise<ActionOutcome> {
  return post("/api/v1/backoffice/participants/erase", {
    participant_id: participantId,
    reason,
  });
}

export type PreviewTestData = components["schemas"]["PreviewTestDataView"];

/** What the test runs of the preview left in one action (D21): counts only. */
export function loadTestData(api: CountdownApiClient, actionId: string) {
  return api.GET("/api/v1/backoffice/actions/test-data", {
    params: { query: { action_id: actionId } },
  });
}

/** Remove the test runs of the preview from one action (D21). The real participants stay. */
export async function purgeTestData(actionId: string): Promise<ActionOutcome> {
  return post("/api/v1/backoffice/actions/test-data/purge", undefined, actionId);
}

export type ShippingAddress = components["schemas"]["ShippingAddressView"];

export type PrizeDoor = components["schemas"]["PrizeDoorRowView"];
export type PrizeDoorDetail = components["schemas"]["PrizeDoorView"];
export type PrizeAllocation = components["schemas"]["PrizeAwardDetailView"];

/** Every door of the action whose day has come, with its prize and next step (D95). */
export function loadPrizeDoors(api: CountdownApiClient, actionId: string) {
  return api.GET("/api/v1/backoffice/prizes/doors", {
    params: { query: { action_id: actionId } },
  });
}

/** Everything about one door's draw (D95); the address stays behind `readShippingAddress`. */
export function loadPrizeDoor(api: CountdownApiClient, actionId: string, position: number) {
  return api.GET("/api/v1/backoffice/prizes/door", {
    params: { query: { action_id: actionId, field_position: position } },
  });
}

/** One mail of a door's draw with its words, the claim link cut out. */
export function loadPrizeDoorMail(
  api: CountdownApiClient,
  actionId: string,
  position: number,
  mailQueueId: string,
) {
  return api.GET("/api/v1/backoffice/prizes/door/mail", {
    params: {
      query: { action_id: actionId, field_position: position, mail_queue_id: mailQueueId },
    },
  });
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** One typed identifier: a participant id if it looks like one, else an address. */
export function participantRef(
  value: string,
): { readonly participant_id: string } | { readonly email: string } {
  return UUID_PATTERN.test(value) ? { participant_id: value } : { email: value };
}

/**
 * One winner's shipping address, and the audit row that records the retrieval.
 *
 * A POST, because on the server this is a recorded act rather than a query: a GET would
 * invite the browser, a proxy and the history to repeat it, and the log would end up
 * counting re-renders instead of decisions. Never cached, never held in a list.
 */
export async function readShippingAddress(
  prizeAwardId: string,
  actionId: string,
): Promise<ShippingAddress | null> {
  try {
    const { data } = await client.POST("/api/v1/backoffice/prizes/address", {
      params: { query: { action_id: actionId } },
      body: { prize_award_id: prizeAwardId },
    });
    return data ?? null;
  } catch (caught: unknown) {
    console.error("[api] address retrieval threw", caught);
    return null;
  }
}

/** »Versendet« (D96), with the tracking note and an optional message to the winner. */
export async function markPrizeShipped(
  prizeAwardId: string,
  note: string | null,
  actionId: string,
  message: string | null = null,
): Promise<ActionOutcome> {
  return post(
    "/api/v1/backoffice/prizes/ship",
    { prize_award_id: prizeAwardId, note, message },
    actionId,
  );
}

export async function markPrizeCompleted(
  prizeAwardId: string,
  actionId: string,
): Promise<ActionOutcome> {
  return post("/api/v1/backoffice/prizes/complete", { prize_award_id: prizeAwardId }, actionId);
}

/** Draw again for a prize that forfeited with nobody after it (D95). */
export async function redrawPrize(prizeAwardId: string, actionId: string): Promise<ActionOutcome> {
  return post("/api/v1/backoffice/prizes/redraw", { prize_award_id: prizeAwardId }, actionId);
}

export type BackofficeBrand = components["schemas"]["BackofficeBrandView"];
export type BrandRequest = components["schemas"]["BrandRequest"];
export type ConsentTemplate = components["schemas"]["ConsentTemplateView"];
export type ConsentType = components["schemas"]["ConsentType"];

export function loadBrands(api: CountdownApiClient) {
  return api.GET("/api/v1/backoffice/brands");
}

/**
 * A brand: its sender, and without a brand catalog also name and colour; `create: true` makes a
 * new one under a key nobody has. `null` keeps a stored value, `""` clears the reply address.
 */
export async function saveBrand(body: BrandRequest): Promise<ActionOutcome> {
  return post("/api/v1/backoffice/brands", body);
}

/** The logo of one brand, named by its key: a new brand has no action to find it through. */
export async function uploadBrandLogo(brandKey: string, file: File): Promise<ImageOutcome> {
  try {
    const { data, response, error } = await client.POST("/api/v1/backoffice/brand/logo", {
      // openapi-typescript maps `format: binary` to `string`; the serializer puts the File.
      body: { file: file as unknown as string, brand_key: brandKey },
      bodySerializer(body: Record<string, unknown> | undefined) {
        const payload = logoForm(body);
        payload.append("brand_key", brandKey);
        return payload;
      },
    });
    if (data === undefined) {
      console.error("[api] brand logo upload failed", response.status);
      return uploadRefusal(response.status, error);
    }
    return { ok: true };
  } catch (caught: unknown) {
    console.error("[api] brand logo upload threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

export type BrandSync = components["schemas"]["BrandSyncView"];

/** Copy key, name, colour and logo from the catalog of the customer portal (D25). */
export async function syncBrands(): Promise<
  { readonly ok: true; readonly report: BrandSync } | { readonly ok: false; readonly message: string }
> {
  try {
    const { data, response, error } = await client.POST("/api/v1/backoffice/brands/sync");
    if (data === undefined) {
      console.error("[api] brand sync failed", response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, report: data };
  } catch (caught: unknown) {
    console.error("[api] brand sync threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

export type Grants = components["schemas"]["GrantsView"];

/** The grants page (D57): every element with the roles that open it, and every column. */
export function loadGrants(api: CountdownApiClient) {
  return api.GET("/api/v1/backoffice/grants");
}

/** The whole matrix; an element without a role is the superadmin's alone. */
export async function saveGrants(grants: Record<string, string[]>): Promise<ActionOutcome> {
  return post("/api/v1/backoffice/grants", { grants });
}

/** A client role of the identity service as a new column, or a new name for one. */
export async function saveGrantRole(roleName: string, label: string): Promise<ActionOutcome> {
  return post("/api/v1/backoffice/grants/roles", { role_name: roleName, label });
}

/** Remove a column and everything it was granted. */
export async function removeGrantRole(roleName: string): Promise<ActionOutcome> {
  return post("/api/v1/backoffice/grants/roles/remove", { role_name: roleName });
}

export function loadConsentTemplates(api: CountdownApiClient) {
  return api.GET("/api/v1/backoffice/consent-templates");
}

/** Always a new approved version; the previous one stays as granted consents quote it. */
export async function approveConsentTemplate(
  consentType: ConsentType,
  bodyTemplate: string,
): Promise<ActionOutcome> {
  return post("/api/v1/backoffice/consent-templates", {
    consent_type: consentType,
    body_template: bodyTemplate,
  });
}

export type ActionDetail = components["schemas"]["ActionDetailView"];
export type ActionCreateRequest = components["schemas"]["ActionCreateRequest"];
export type LeadFieldBody = components["schemas"]["LeadFieldModel"];
export type LeadFieldsRequest = components["schemas"]["LeadFieldsRequest"];

export function loadActionDetail(api: CountdownApiClient, actionId: string) {
  return api.GET("/api/v1/backoffice/actions/detail", {
    params: { query: { action_id: actionId } },
  });
}

/** A new draft with its doors laid out. The answer carries the id the screen navigates to. */
export async function createAction(
  body: ActionCreateRequest,
): Promise<
  | { readonly ok: true; readonly detail: ActionDetail }
  | { readonly ok: false; readonly message: string }
> {
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/actions",
      { body },
    );
    if (data === undefined) {
      console.error("[api] action create failed", response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, detail: data };
  } catch (caught: unknown) {
    console.error("[api] action create threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

export type CustomerActionRequest = components["schemas"]["CustomerActionRequest"];

/** A copy of a template for one end customer, every door booked by them, the standard content
 * released. The answer carries the id the screen navigates to. */
export async function createCustomerAction(
  body: CustomerActionRequest,
): Promise<
  | { readonly ok: true; readonly detail: ActionDetail }
  | { readonly ok: false; readonly message: string }
> {
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/actions/customer",
      { body },
    );
    if (data === undefined) {
      console.error("[api] customer action create failed", response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, detail: data };
  } catch (caught: unknown) {
    console.error("[api] customer action create threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/** The settings sections of one action; each is its own write with its own audit row. */
interface ActionSectionBodies {
  readonly basics: components["schemas"]["ActionBasicsRequest"];
  readonly schedule: components["schemas"]["ActionScheduleRequest"];
  readonly organizer: components["schemas"]["OrganizerRequest"];
  readonly "calendar-layout": components["schemas"]["CalendarLayoutRequest"];
  readonly "door-number-style": components["schemas"]["DoorNumberStyleRequest"];
  readonly "calendar-style": components["schemas"]["CalendarStyleRequest"];
  readonly "placeholder-image/remove": undefined;
  readonly "account-offer": components["schemas"]["AccountOfferRequest"];
  readonly "embed-origins": components["schemas"]["EmbedOriginsRequest"];
  readonly "embed-look": components["schemas"]["EmbedLookRequest"];
  readonly identity: components["schemas"]["IdentityRequest"];
  readonly terms: components["schemas"]["TermsRequest"];
  readonly "test-flag": components["schemas"]["ActionTestFlagRequest"];
  readonly "sponsor-workflow": components["schemas"]["SponsorWorkflowRequest"];
  readonly notifications: components["schemas"]["NotificationSettingsRequest"];
}

export async function saveActionSection<S extends keyof ActionSectionBodies>(
  section: S,
  body: ActionSectionBodies[S],
  actionId: string,
): Promise<ActionOutcome> {
  return post(`/api/v1/backoffice/actions/${section}`, body, actionId);
}

/**
 * The registration form of one action, whole and in order (D23).
 *
 * Its own function rather than {@link saveActionSection}: the editor needs the answer. A
 * field of the action's own is sent without a key and gets one only there; a form that kept
 * what it sent would add the field a second time on the next save.
 */
export async function saveLeadFields(
  body: LeadFieldsRequest,
  actionId: string,
): Promise<
  | { readonly ok: true; readonly detail: ActionDetail }
  | { readonly ok: false; readonly message: string }
> {
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/actions/lead-fields",
      { params: { query: { action_id: actionId } }, body },
    );
    if (data === undefined) {
      console.error("[api] lead fields save failed", response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, detail: data };
  } catch (caught: unknown) {
    console.error("[api] lead fields save threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

export type StyleGroup = components["schemas"]["StyleTransferRequest"]["groups"][number];

/**
 * The look of one action for others, by group (D37). Answers with the actions that took it
 * and those that kept their form because their first door has opened (D24); a refusal names
 * every ended target, one per line, and nothing is written. A target past draft is refused the
 * same way when the form would make a consent required that has no released wording.
 */
export async function applyStyle(
  body: components["schemas"]["StyleTransferRequest"],
): Promise<
  | {
      readonly ok: true;
      readonly actionIds: readonly string[];
      readonly formKept: readonly string[];
    }
  | { readonly ok: false; readonly message: string }
> {
  try {
    const { data, response, error } = await client.POST(
      "/api/v1/backoffice/actions/apply-style",
      { body },
    );
    if (data === undefined) {
      console.error("[api] style transfer failed", response.status);
      return { ok: false, message: outcomeMessage(response.status, error) };
    }
    return { ok: true, actionIds: data.action_ids, formKept: data.form_kept };
  } catch (caught: unknown) {
    console.error("[api] style transfer threw", caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/**
 * Go live, finish, archive or reactivate. A refused go-live answers with every obstacle, one
 * per line; the other refusals with the one reason.
 */
export async function moveAction(
  step: "publish" | "finish" | "archive" | "reactivate",
  actionId: string,
): Promise<ActionOutcome> {
  return post(`/api/v1/backoffice/actions/${step}`, undefined, actionId);
}

/** One JSON write, with the shared status handling. `actionId` becomes `?action_id=`. */
async function post(
  path: string,
  body: unknown,
  actionId?: string,
): Promise<ActionOutcome> {
  try {
    const { data, response, error } = await client.POST(
      path as never,
      { body, params: { query: { action_id: actionId } } } as never,
    );
    if (data !== undefined) {
      return { ok: true };
    }
    console.error("[api] write failed", path, response.status);
    return { ok: false, message: outcomeMessage(response.status, error) };
  } catch (caught: unknown) {
    console.error("[api] write threw", path, caught);
    return { ok: false, message: LOAD_FAILED };
  }
}

/**
 * The API's own sentence when there is one.
 *
 * Passed through rather than replaced, like {@link runEditorialAction} does: these endpoints
 * sit behind a role and the reason ("der Inhalt ist eingefroren", "das Format wird nicht
 * unterstützt") is exactly what the person at the screen has to act on.
 */
export function outcomeMessage(status: number, error: unknown): string {
  if (status === 401) {
    return "Die Anmeldung ist abgelaufen. Bitte neu anmelden.";
  }
  if (status === 403) {
    return ROLE_MISSING;
  }
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === "string" && detail) {
    return detail;
  }
  // A field list from the request validation. Only the API's own validators write German;
  // the built-in messages are English and stay behind the generic sentence.
  const own = "Value error, ";
  const first: unknown = Array.isArray(detail)
    ? (detail[0] as { msg?: unknown } | undefined)?.msg
    : undefined;
  if (typeof first === "string" && first.startsWith(own)) {
    return first.slice(own.length);
  }
  // A 413 without a detail is the proxy's, not the API's: its answer is an HTML page.
  if (status === 413) {
    return "Die Datei ist zu groß.";
  }
  return status === 422 ? "Die Eingabe ist ungültig." : LOAD_FAILED;
}
